import { Component, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { AuthService } from 'src/app/core/auth/auth.service';
import { PermissionService } from 'src/app/core/permission.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

import { GlobalDataService } from 'src/app/core/global-data/global-data.service';
import { UtilService } from 'src/app/shared/util.service';
import { combineLatest, Subject, takeUntil, tap } from 'rxjs';
import * as svgIcons from '@progress/kendo-svg-icons';
import { AuthUser } from 'src/app/core/auth/auth-user.model';
import { CustomerAddress } from 'src/app/module/customer-address/customer-address.model';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  public allIcons = svgIcons;

  private unsubNotifier = new Subject<void>();

  isLoading = false;
  langue = this.translate.currentLang
  remainingBudget:number = 0;
  budgetTotal: number = 0;
  canSeeRemainingBudget: boolean = false; // permission
  showBudget = false; // limitation budget
  logState:boolean = false
  numberOfItemsInCart:number | undefined  = undefined;
  EmployeeVisibleNotes: string = '';
  ShowEmployeeVisibleNotes: boolean = false;
  canSeeCartbutton: boolean = false;
  deductTransportFeeFromBudget: boolean = false;
  transportFeeAmountFormatted: string = '0.00$';
  isCurrentlyImpersonating: boolean = false;
  cartData: any = null;
  shippingInfo: any = null;
  currentShipTo: CustomerAddress | null = null;
  currentCatalog: any = null;
  customerList: any[] = [];
  userGroupList: any[] = [];
  canChangeCatalog: boolean = false;

  @Output() toggleSidenav = new EventEmitter();

  constructor(
    public authService: AuthService,
    public permissionService: PermissionService,
    private translate: TranslateService,
    private router: Router,
    private globalDataService: GlobalDataService,
    private utilService: UtilService,
  ) {}

  ngOnInit(): void {
    //on va ÉCOUTER le changement de langue, et déclencher un refresh au besoin
    this.translate.onLangChange
      .pipe(takeUntil(this.unsubNotifier))
      .subscribe(event => {
        this.langue = event.lang;
    });

    // assigner variable du catalogue courant
    this.globalDataService.currentCatalog
    .pipe(takeUntil(this.unsubNotifier))
    .subscribe((catalog) => {
      this.currentCatalog = catalog;
    });

    // assigner variable du shipto courant
    this.globalDataService.currentShipTo
    .pipe(takeUntil(this.unsubNotifier))
    .subscribe((shipTo) => {
      this.currentShipTo = shipTo;
    });

    // customer list
    this.globalDataService.customerList
    .pipe(takeUntil(this.unsubNotifier))
    .subscribe((customers) => {
      this.customerList = customers;
      // la liste des clients influence le filtrage des catalogues (flag onlyShowCustomerCatalogs)
      this.updateCanChangeCatalog();
    });

    // usergroup list
    this.globalDataService.userGroupList
    .pipe(takeUntil(this.unsubNotifier))
    .subscribe((userGroups) => {
      this.userGroupList = userGroups;
    });

    // load des infos sur le groupe usager de l'usager et ses commandes.
    let uoo = this.globalDataService.userOrderOverview.asObservable();
    let ugr = this.globalDataService.userGroupRestrictions.asObservable();
    let cartData = this.globalDataService.cartData.asObservable();
    let currentShipTo = this.globalDataService.currentShipTo.asObservable();

    // fetch async
    let myAsyncData = combineLatest([uoo,ugr,cartData,currentShipTo]);
    myAsyncData.pipe(takeUntil(this.unsubNotifier)).subscribe(([uoo,ugr,cartData, currentShipTo]) => {
        // attendre que le UOO et UDR soient chargés
        if (uoo.actualPurchasedAmount != -1 && ugr.userGroupHeaderId != 0) {
          // CLIENT EMPLOYE
          this.remainingBudget = this.globalDataService.getRemainingBudget(); // set du budget restant #1 (sans frais de transport)
          this.canSeeRemainingBudget = this.globalDataService.canUserSeeHeaderBudget_sync();
          this.showBudget = this.globalDataService.budgetAmountLimitationActivated.getValue();
          // usergroup & user order overview data
          this.deductTransportFeeFromBudget = ugr.deductTransportFeeFromBudget;
          this.budgetTotal = uoo.amountRemaining > 0 ? uoo.amountRemaining : 0; 
          // ship to
          if (currentShipTo && currentShipTo.shipToId) {
            this.currentShipTo = currentShipTo;
            // get employee totals
            var totals = this.globalDataService.getClientEmployeCurrentTotals(cartData, ugr, uoo, currentShipTo);
            this.remainingBudget = totals.remainingBudget; // set du budget restant #2
            this.transportFeeAmountFormatted = totals.transportFeeAmountFormatted;
            this.numberOfItemsInCart = totals.itemQuantity;
          }
        }
        else {
          // SUPERVISOR / ADMIN
          let { total, itemQuantity } = this.globalDataService.getCartTotalAndQuantity();
          this.numberOfItemsInCart = itemQuantity;
          this.canSeeRemainingBudget = false;
          this.showBudget = false;
        }
      }
    );

    //on va ÉCOUTER le changement d'uysager en cours, et déclencher un refresh au besoin.  Le déclenchement se fait dans authService, via une méthode qui fait un next sur le currentAuthUserBehavior
    this.authService.currentAuthUserBehavior
    .pipe(
      tap((x) => {
        //une fois que l'usager est changé, on rafraichit le header

        // Can user change catalog
        this.updateCanChangeCatalog();

        this.canSeeCartbutton = this.permissionService.canSeeCartButton();
        this.isCurrentlyImpersonating = this.authService.isCurrentlyImpersonating();
        
        //on va chercher la note de l'usager
        this.EmployeeVisibleNotes = x.employeePersonnalComment ?? '';

        //on va convertir les \n en html : 
        this.EmployeeVisibleNotes = this.EmployeeVisibleNotes.replace(/\n/g, '<br/>');
      
        // Si on a une note on débarre le bouton
        if(this.EmployeeVisibleNotes.length > 0)
          this.ShowEmployeeVisibleNotes = true;
        else
          this.ShowEmployeeVisibleNotes = false;
      })
      ,takeUntil(this.unsubNotifier)
    )
    .subscribe({});
  }

  ngOnDestroy(): void {
    this.unsubNotifier.next();
    this.unsubNotifier.unsubscribe();
  }

  selectLangue(langue: string) {
    this.langue = langue;
    this.translate.use(langue)
  }

  OnLogoutClick() {
    this.logState = false;
    this.globalDataService.clearGlobalData();

    this.authService.logout(true)
      .pipe(
        tap(() => {
          this.router.navigate(['/auth']);
        }),
        takeUntil(this.unsubNotifier))
      .subscribe();
  }

  /**
   * Cette méthode permet de retourner à l'usager original.  le JWT sera changé pour celui de l'usager original.
   */
  OnClickStopImpersonate() {

    // fix erreur js/kendo composant menu qui ne tolère pas de faire disparaitre le bouton Impersonate alors que le menu ferme. Ça force à fermer le menu avant que le bouton ne soit retiré du DOM.
    document.body.click();

    this.isCurrentlyImpersonating = false;

    if (this.authService.isCurrentlyImpersonating()) {

      this.isLoading = true;
      //on détruit toute les données en localStorage de l'usager usurpé
      this.globalDataService.clearGlobalData();

      //on détruit le JWT de l'usager usurpé
      this.globalDataService.StopImpersonationAndRefreshData().subscribe((x) => {});

      // redirect home
      this.router.navigate(['/auth/home']);
    }
  }

  SeeUserPersonnalNotes() {
    let title = 'module.user.model.personalComment';
    let msg = this.EmployeeVisibleNotes;
    this.utilService.alert(title, msg);
  }

  selectShipTo() {
    this.router.navigate(['/checkout']);
  }

  selectCatalog() {
    if (this.canChangeCatalog) {
      this.router.navigate(['/catalog/selection']);
    }
  }

  /**
   * Détermine si l'usager peut changer de catalogue : uniquement un Superviseur qui a plus d'un catalogue
   * dans sa liste filtrée (les catalogues des User Group masqués par le flag onlyShowCustomerCatalogs ne comptent pas).
   */
  private updateCanChangeCatalog() {
    var isClientEmploye = this.authService.GetCurrentUser().isClientEmploye;
    if (isClientEmploye) {
      this.canChangeCatalog = false;
      return;
    }
    var catalogList = this.globalDataService.filterCatalogListForSupervisor(
      this.authService.GetCurrentUser().availableCatalogList,
      this.customerList
    );
    this.canChangeCatalog = catalogList.length > 1;
  }

  /** Cette méthode permet d'aller chercher la version 'live' de l'usager connecté (pas via un subscription).  Pratique pour tester localement 'IsLogged... avoir le nom/prénom, etc,  sans avoir à lier le TemplateHTML à authhService
   * @returns
   */
  public currentUser(): AuthUser {return this.authService.GetCurrentUser()};
}

